/**
 * Spotify strip: album art, track, transport controls. Collapsed to a slim bar
 * during navigation; tapping art expands to a fuller card with volume and
 * "play here" (transfer playback into the car).
 */

import type { NowPlaying, SpotifyPlayer } from '../audio/spotify';
import { beginLogin, isAuthed } from '../audio/spotify';
import { hasSpotify } from '../core/config';
import { t } from '../core/i18n';

export class SpotifyBar {
  readonly el: HTMLElement;
  private expanded = false;
  private state: NowPlaying | null = null;

  constructor(
    parent: HTMLElement,
    private player: SpotifyPlayer,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'spotify';
    parent.appendChild(this.el);
    this.render();
  }

  update(state: NowPlaying | null): void {
    this.state = state;
    this.render();
  }

  private render(): void {
    if (!hasSpotify()) {
      this.el.hidden = true;
      return;
    }
    this.el.hidden = false;

    if (!isAuthed()) {
      this.el.className = 'spotify spotify--connect';
      this.el.innerHTML = `
        <button class="spotify__connect-btn">${SPOTIFY_LOGO}<span>${t('sp.connect')}</span></button>`;
      this.el.querySelector('button')!.addEventListener('click', () => void beginLogin());
      return;
    }

    const s = this.state;
    this.el.className = `spotify ${this.expanded ? 'spotify--expanded' : ''}`;
    this.el.innerHTML = `
      <div class="spotify__bar">
        <button class="spotify__art" aria-label="Expand player">
          ${s?.albumArt ? `<img src="${s.albumArt}" alt=""/>` : SPOTIFY_LOGO}
        </button>
        <div class="spotify__track">
          <div class="spotify__name">${escapeHtml(s?.trackName || t('sp.nothing'))}</div>
          <div class="spotify__artist">${escapeHtml(s?.artists || (s?.deviceName ? t('sp.on', { device: s.deviceName }) : t('sp.hint')))}</div>
        </div>
        <div class="spotify__controls">
          <button class="icon-btn" data-act="prev" aria-label="Previous">${PREV_SVG}</button>
          <button class="icon-btn spotify__play" data-act="play" aria-label="Play or pause">
            ${s && !s.paused ? PAUSE_SVG : PLAY_SVG}
          </button>
          <button class="icon-btn" data-act="next" aria-label="Next">${NEXT_SVG}</button>
        </div>
      </div>
      ${
        this.expanded
          ? `<div class="spotify__extra">
              ${
                s && !s.playingHere
                  ? `<button class="btn btn--ghost btn--small" data-act="here">${t('sp.playHere')}</button>`
                  : `<span class="spotify__device">${t('sp.playingHere')}</span>`
              }
              <label class="spotify__vol">${VOL_SVG}
                <input type="range" min="0" max="100" value="80" data-act="vol"/>
              </label>
            </div>`
          : ''
      }`;

    this.el.querySelector('.spotify__art')!.addEventListener('click', () => {
      this.expanded = !this.expanded;
      this.render();
    });
    this.on('play', () => void this.player.togglePlay());
    this.on('next', () => void this.player.next());
    this.on('prev', () => void this.player.previous());
    this.on('here', () => void this.player.transferHere());
    this.el.querySelector('[data-act=vol]')?.addEventListener('change', (e) => {
      void this.player.setVolume(Number((e.target as HTMLInputElement).value) / 100);
    });
  }

  private on(act: string, fn: () => void): void {
    this.el.querySelector(`[data-act=${act}]`)?.addEventListener('click', fn);
  }
}

const SPOTIFY_LOGO = `<svg viewBox="0 0 24 24" class="spotify__logo"><circle cx="12" cy="12" r="10" fill="#1DB954"/><path d="M7 9.5c3.2-1 7-.6 9.6 1M7.5 12.3c2.6-.8 5.6-.4 7.8.9M8 15c2-.6 4.3-.3 6 .7" stroke="#07090d" stroke-width="1.5" stroke-linecap="round" fill="none"/></svg>`;
const PLAY_SVG = `<svg viewBox="0 0 24 24"><path d="M8 5 L19 12 L8 19 Z" fill="currentColor"/></svg>`;
const PAUSE_SVG = `<svg viewBox="0 0 24 24"><path d="M7 5 h3.4 v14 H7 Z M13.6 5 H17 v14 h-3.4 Z" fill="currentColor"/></svg>`;
const NEXT_SVG = `<svg viewBox="0 0 24 24"><path d="M6 5 L14 12 L6 19 Z M15.5 5 h2.5 v14 h-2.5 Z" fill="currentColor"/></svg>`;
const PREV_SVG = `<svg viewBox="0 0 24 24"><path d="M18 5 L10 12 L18 19 Z M6 5 h2.5 v14 H6 Z" fill="currentColor"/></svg>`;
const VOL_SVG = `<svg viewBox="0 0 24 24"><path d="M4 9 v6 h4 l5 4 V5 L8 9 Z" fill="currentColor"/><path d="M16 9 a4 4 0 0 1 0 6" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linecap="round"/></svg>`;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
